import { create } from 'zustand';
import { enhancedChatApi } from 'src/utils/enhancedChatApi';
import { Journey } from 'src/common/interfaces/journey.interface';
import { Organization } from 'src/common/interfaces/organization.interface';
import { RenderMode } from '../types';
import { Nullable } from 'src/types/nullable.type';
import { useCurrentSubscription } from 'src/store/subscription';

// Generic API result type for enhancedChatApi calls
type ApiResult<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

export interface EnhancedChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  metadata?: {
    source_content_uuid?: string;
    source_node_uuid?: string;
    confidence_score?: number;
    retrieval_context?: Array<{
      key: string;
      similarity: number;
      content_type: string;
      content_uuid?: string;
    }>;
  };
  feedback?: {
    type: 'thumbs_up' | 'thumbs_down';
    comment?: string;
  };
}

export interface ConversationContext {
  name: string;
  creator: string;
  organization: string;
  updated_at: string;
}

interface EnhancedChatState {
  // Conversation state
  conversationId: string | null;
  messages: EnhancedChatMessage[];
  isLoading: boolean;
  error: string | null;
  
  // UI state
  isOpen: boolean;
  isThinking: boolean;
  inputValue: string;
  
  // Journey context
  journeyUUID: string;
  journeyContext: ConversationContext | null;
  
  // Chat settings from journey
  assistantName: string;
  personality: string;
  disabled: boolean;
  shouldGreetVisitors: boolean;
  avatarUrl: string;
  
  // Enhanced features
  potentialQuestions: string[];
  conversationMetrics: {
    messageCount: number;
    lastUpdated: string | null;
  };
  
  // User context
  visitorEmail: string;
  userContext: Record<string, any>;
  renderMode: Nullable<RenderMode>;
  
  // Initialization state
  isInitialized: boolean;
  isInitializing: boolean;
}

interface EnhancedChatActions {
  // Conversation management
  startConversation: (journeyUUID: string, visitorEmail?: string, userContext?: Record<string, any>) => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  loadConversationHistory: (conversationId: string) => Promise<void>;
  endConversation: () => void;
  
  // UI actions
  setIsOpen: (isOpen: boolean) => void;
  setInputValue: (value: string) => void;
  
  // Settings
  initializeFromJourney: (journey: Journey, organization?: Organization, renderMode?: RenderMode) => void;
  updateSettings: (settings: Partial<{
    assistantName: string;
    personality: string;
    disabled: boolean;
    shouldGreetVisitors: boolean;
    avatarUrl: string;
  }>) => void;
  
  // Feedback
  submitMessageFeedback: (messageId: string, feedbackType: 'thumbs_up' | 'thumbs_down', comment?: string) => Promise<void>;
  
  // Utility
  reset: () => void;
  clearError: () => void;
  regenerateEmbeddings: () => Promise<void>;
}

type EnhancedChatStore = EnhancedChatState & EnhancedChatActions;

const initialState: EnhancedChatState = {
  conversationId: null,
  messages: [],
  isLoading: false,
  error: null,
  
  isOpen: false,
  isThinking: false,
  inputValue: '',
  
  journeyUUID: '',
  journeyContext: null,
  
  assistantName: 'Journey Assistant',
  personality: '',
  disabled: false,
  shouldGreetVisitors: false,
  avatarUrl: '',
  
  potentialQuestions: [],
  conversationMetrics: {
    messageCount: 0,
    lastUpdated: null,
  },
  
  visitorEmail: '',
  userContext: {},
  renderMode: null,
  
  isInitialized: false,
  isInitializing: false,
};

export const useEnhancedChatStore = create<EnhancedChatStore>((set, get) => ({
  ...initialState,

  startConversation: async (journeyUUID: string, visitorEmail?: string, userContext?: Record<string, any>) => {
    const state = get();
    
    if (state.isInitializing) {
      return; // Already initializing
    }
    
    // Clear any existing conversation to start fresh
    if (state.conversationId) {
      set({
        conversationId: null,
        messages: [],
        potentialQuestions: [],
        conversationMetrics: {
          messageCount: 0,
          lastUpdated: null,
        },
        isInitialized: false,
      });
    }
    
    set({ isInitializing: true, error: null });
    
    try {
      const result = (await enhancedChatApi.startConversation({
        journey_uuid: journeyUUID,
        visitor_email: visitorEmail,
        user_context: userContext,
      })) as ApiResult<{
        conversation_id: string;
        greeting: { content: string; metadata?: any };
        potential_questions: string[];
        journey_context: any;
      }>;
      
      if (result.success && result.data) {
        const { conversation_id, greeting, potential_questions, journey_context } = result.data;
        
        const greetingMessage: EnhancedChatMessage = {
          id: `greeting-${Date.now()}`,
          role: 'assistant',
          content: greeting.content,
          timestamp: new Date().toISOString(),
          metadata: greeting.metadata || {},
        };
        
        set({
          conversationId: conversation_id,
          messages: [greetingMessage],
          potentialQuestions: potential_questions || [],
          journeyContext: journey_context,
          journeyUUID,
          visitorEmail: visitorEmail || '',
          userContext: userContext || {},
          isInitialized: true,
          isInitializing: false,
          conversationMetrics: {
            messageCount: 1,
            lastUpdated: new Date().toISOString(),
          },
        });
      } else {
        throw new Error(result.error || 'Failed to start conversation');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to start conversation',
        isInitializing: false,
        isInitialized: true,
      });
    }
  },

  sendMessage: async (message: string) => {
    const state = get();
    
    if (!state.journeyUUID || state.isThinking) {
      return;
    }
    
    // Add user message immediately
    const userMessage: EnhancedChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    
    set({
      messages: [...state.messages, userMessage],
      inputValue: '',
      isThinking: true,
      error: null,
    });
    
    try {
      const result = (await enhancedChatApi.sendMessage({
        journey_uuid: state.journeyUUID,
        message,
        visitor_email: state.visitorEmail || undefined,
        user_context: state.userContext || undefined,
      })) as ApiResult<{
        answer: string;
        source_content_uuid?: string;
        source_node_uuid?: string;
        confidence_score?: number;
        retrieval_context?: Array<any>;
      }>;
      
      if (result.success && result.data) {
        const {
          answer,
          source_content_uuid,
          source_node_uuid,
          confidence_score,
        } = result.data;
        
        const assistantMessage: EnhancedChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: answer,
          timestamp: new Date().toISOString(),
          metadata: {
            source_content_uuid,
            source_node_uuid,
            confidence_score,
          },
        };
        
        set({
          messages: [...get().messages, assistantMessage],
          // Potential questions are no longer returned in stateless mode
          potentialQuestions: [],
          conversationMetrics: {
            messageCount: state.conversationMetrics.messageCount + 2,
            lastUpdated: new Date().toISOString(),
          },
          isThinking: false,
        });
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      const errorMessage: EnhancedChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: "I'm sorry, I encountered an error while processing your message. Please try again.",
        timestamp: new Date().toISOString(),
      };
      
      set({
        messages: [...get().messages, errorMessage],
        error: error instanceof Error ? error.message : 'Failed to send message',
        isThinking: false,
      });
    }
  },

  loadConversationHistory: async (conversationId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const result = (await enhancedChatApi.getConversationHistory(conversationId)) as ApiResult<{
        messages: Array<{ id: string; role: string; content: string; timestamp: string; metadata?: any }>;
        total_messages: number;
        journey: { uuid: string; name: string };
        created_at: string;
        last_message_at: string;
      }>;
      
      if (result.success && result.data) {
        const { messages, total_messages, journey, created_at, last_message_at } = result.data;
        
        const enhancedMessages: EnhancedChatMessage[] = messages.map((msg: any) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp,
          metadata: msg.metadata || {},
        }));
        
        set({
          conversationId,
          messages: enhancedMessages,
          conversationMetrics: {
            messageCount: total_messages,
            lastUpdated: last_message_at,
          },
          journeyUUID: journey.uuid,
          isLoading: false,
          isInitialized: true,
        });
      } else {
        throw new Error(result.error || 'Failed to load conversation history');
      }
    } catch (error) {
      console.error('Error loading conversation history:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load conversation history',
        isLoading: false,
      });
    }
  },

  endConversation: () => {
    set({
      conversationId: null,
      messages: [],
      potentialQuestions: [],
      conversationMetrics: {
        messageCount: 0,
        lastUpdated: null,
      },
      isInitialized: false,
      isOpen: false,
    });
  },

  setIsOpen: (isOpen: boolean) => {
    set({ isOpen });
  },

  setInputValue: (inputValue: string) => {
    set({ inputValue });
  },

  initializeFromJourney: (journey: Journey, organization?: Organization, renderMode?: RenderMode) => {
    const journeyData = journey.data || {};
    const talkToJourneySettings = journeyData['talk_to_journey'] || {};
    
    // Check if user has access to chatbot based on subscription plan
    const { subscriptionPlanType, isOnTrial } = useCurrentSubscription.getState();
    const hasChatbotAccess = (subscriptionPlanType === 'pro' || subscriptionPlanType === 'enterprise') || isOnTrial;
    
    // Only force-disable for new journeys (when talk_to_journey settings don't exist)
    // For existing journeys, respect the saved state from the backend
    const isNewJourney = !talkToJourneySettings || Object.keys(talkToJourneySettings).length === 0;
    const shouldDisableChatbot = isNewJourney && !hasChatbotAccess;
    const finalDisabledState = shouldDisableChatbot ? true : (talkToJourneySettings.disabled || false);
    
    set({
      journeyUUID: journey.uuid,
      assistantName: talkToJourneySettings.name || 'Journey Assistant',
      personality: talkToJourneySettings.personality || '',
      shouldGreetVisitors: talkToJourneySettings.shouldGreetVisitors === true,
      disabled: finalDisabledState,
      avatarUrl: talkToJourneySettings.avatarUrl || '',
      renderMode,
      journeyContext: {
        name: journey.name,
        creator: journey.creator?.name || journey.creator?.email || 'Unknown',
        organization: organization?.name || journey.organization?.name || 'Unknown',
        updated_at: journey.updated_at,
      },
    });
  },

  updateSettings: (settings) => {
    set((state) => ({
      ...state,
      ...settings,
    }));
  },

  clearChat: () => {
    set({
      conversationId: null,
      messages: [],
      potentialQuestions: [],
      conversationMetrics: {
        messageCount: 0,
        lastUpdated: null,
      },
      isInitialized: false,
      isOpen: false,
    });
  },

  submitMessageFeedback: async (messageId: string, feedbackType: 'thumbs_up' | 'thumbs_down', comment?: string) => {
    try {
      const result = (await enhancedChatApi.submitMessageFeedback({
        message_id: messageId,
        feedback_type: feedbackType,
        comment,
      })) as ApiResult<any>;
      
      if (result.success) {
        // Update the message with feedback
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  feedback: {
                    type: feedbackType,
                    comment,
                  },
                }
              : msg
          ),
        }));
      } else {
        throw new Error(result.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to submit feedback',
      });
    }
  },

  regenerateEmbeddings: async () => {
    const state = get();
    
    if (!state.conversationId) {
      return;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      const result = (await enhancedChatApi.regenerateEmbeddings(state.conversationId)) as ApiResult<any>;
      
      if (result.success) {
        set({ isLoading: false });
      } else {
        throw new Error(result.error || 'Failed to regenerate embeddings');
      }
    } catch (error) {
      console.error('Error regenerating embeddings:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to regenerate embeddings',
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },
})); 